<template>
  <div>
    <div class="has-image-quiz" v-if="isQuizImageVisible">
      <div class="image-quiz__wrapper">
        <!-- question image -->
        <img
          :src="slideActive.image.xLarge"
          alt=""
          class="quiz-image"
          :class="{'hints-default': isHintsDefaultVisible,
                   'showing-hints': showCanvasHints && isQuizWithHints,
                   'bg-base-0': isQuizWithHints}"
          @click="hasHints ? handleShowHints() : false"
        >
        <!-- hints -->
        <img
          src="@/assets/images/hints/default-presenting.svg"
          alt="canvas-hints-presenting"
          class="hints-default-image"
          v-if="isQuizWithHints"
          :class="{'slide-in': hasClassSlideIn, 'hints-closed': !hasClassSlideIn && isHintsClosed}"
          @click="handleShowHints()"
        >
        <CanvasHints
          class="hints-swiper"
          v-if="slideActive.isHintsVisible"
          :class="{'showing-hints': showCanvasHints && isCanvasHintsVisible,
                   'hints-closed': isHintsClosed}"
          @closeCanvasHints="handleCloseHints()"
        />
      </div>
    </div>
    <!-- No question image, just hints -->
    <div v-if="isHintNoQuestionImageVisible" class="has-hints-quiz">
      <img
        src="@/assets/images/hints/default.svg"
        alt="canvas-hints-presenting"
        v-if="isHintsDefaultVisible"
        class="hints-default-image"
        @click="handleShowHints()"
      >
      <CanvasHints
        v-if="showCanvasHints"
        class="animate__animated animate__fadeIn animate__faster hints-swiper"
        :class="{'showing-hints': showCanvasHints}"
        @closeCanvasHints="handleCloseHints()"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CanvasHints from '@/components-v2/presentation-editor/middle/hints/CanvasHints.vue'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import QuizStatuses from '@/constant/QuizStatuses'
import EventBus from '@/eventBus'
import socketHelper from '@/helpers/socket/socket-helper'

export default {
  components: {
    CanvasHints,
  },
  props: {
    everyoneVoted: {
      type: Boolean,
      default: false,
    },
    countTimeToAnswer: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      showCanvasHints: false,
      hasClassSlideIn: false,
      isHintsClosed: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    isQuizPlayingOnPresenterLead() {
      return this.countTimeToAnswer > 3 && !this.everyoneVoted &&
        this.slideActive.quizStatus !== QuizStatuses.RESULT_SCREEN && !this.isAudiencePacing
    },
    isQuizImageVisible() {
      const { slideType, image, hideResult } = this.slideActive
      const slideTypes = ['matchPairs', 'correctOrder']
      return !slideTypes.includes(slideType) && image && hideResult
        && this.countTimeToAnswer > 3 && !this.everyoneVoted &&
        this.slideActive.quizStatus !== QuizStatuses.RESULT_SCREEN
    },
    hasHints() {
      return this.slideActive.isHintsVisible && this.slideActive.hints &&
        this.slideActive.hints.length && this.slideActive.hints.some((hint) => hint.title && hint.title.length)
        && this.isQuizWithHints
    },
    isHintsDefaultVisible() {
      return this.hasHints && !this.showCanvasHints && this.isQuizPlayingOnPresenterLead
    },
    isCanvasHintsVisible() {
      return this.showCanvasHints && this.isQuizPlayingOnPresenterLead
    },
    isAudiencePacing() {
      return this.presentation.audiencePacing
    },
    isHintNoQuestionImageVisible() {
      return !this.isQuizImageVisible & this.isQuizPlayingOnPresenterLead && this.isQuizWithHints
    },
    isPickAnswerOrPickImage() {
      const { type, slideType } = this.slideActive
      return type === 'pickAnswer' && (!slideType || slideType === 'imageChoice')
    },
    isQuizWithHints() {
      return this.isPickAnswerOrPickImage && this.slideActive.isHintsVisible &&
        !this.isAudiencePacing
    },
  },
  mounted() {
    setTimeout(() => {
      this.hasClassSlideIn = true
    }, 1000)
    EventBus.$on('canvas-hints: show-hints-from-backstage', () => {
      this.handleShowHints(true) // alreadyEmitSocketToPresenter = true
    })
  },
  destroyed() {
    EventBus.$off('canvas-hints: show-hints-from-backstage')
  },
  methods: {
    handleShowHints(alreadyEmitSocketToPresenter) {
      this.showCanvasHints = true
      this.hasClassSlideIn = false
      this.isHintsClosed = false
      setTimeout(() => {
        EventBus.$emit('canvas-hints: emit-to-audience')
      }, 200)
      Utils.mixpanelTracking(
        eventCategories.PRESENTING_MODE,
        'Show hint',
        'Show',
        '',
        {
          slideType: Utils.getCorrectSlideTypeName(this.slideActive.type,
            this.slideActive.slideType),
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
        },
      )
      if (alreadyEmitSocketToPresenter) return
      this.emitSocketBetweenPresenterAndBackstage()
    },
    emitSocketBetweenPresenterAndBackstage() {
      socketHelper.backstageControl({
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        isForAudience: false,
        type: 'backstage-control: hints',
        data: 'show-hints',
      })
    },
    handleCloseHints() {
      this.showCanvasHints = false
      this.isHintsClosed = true
      Utils.mixpanelTracking(
        eventCategories.PRESENTING_MODE,
        'Show hint',
        'Close',
        '',
        {
          slideType: Utils.getCorrectSlideTypeName(this.slideActive.type,
            this.slideActive.slideType),
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.has-image-quiz {
  position: absolute;
  top: 10px;
  left: 50%;
  width: 800px;
  margin-left: -400px;
  z-index: 10;

  img {
    max-height: 210px;
    display: block;
    margin: 0 auto;
    max-width: 800px;
  }
  .image-quiz__wrapper {
    width: max-content;
    margin: 0 auto;
    display: flex;
    .quiz-image {
      z-index: 1;
      &.hints-default {
        cursor: pointer;
      }
      &.showing-hints {
        transform: translateX(-200px);
        transition: 0.2s ease;
      }
    }
    .hints-default-image {
      cursor: pointer;
      width: 200px;
      margin-left: -6rem;
      transform: translateX(-58%) rotate(-45deg);
      opacity: 0;
      &.slide-in {
          opacity: 1;
          transform: unset;
          transition: all 0.2s ease;
      }
      &.hints-closed {
        opacity: 1;
        transform: unset;
        transition: opacity 0.2s ease;
      }
    }
    .hints-swiper {
      transform: translateX(95px) rotate(8deg);
      opacity: 0;
      z-index: -1;
      &.hints-closed {
        transform: translateX(150px) translateY(30px) rotate(8deg);
        opacity: 0;
        transition: transform 0.05s ease, opacity 0.1s ease;
      }
      &.showing-hints {
        height: 210px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 1;
        transform: translateX(200px) rotate(0);
        z-index: 0;
        transition: all 0.2s ease;
      }
    }
  }
}
.has-hints-quiz {
  position: absolute;
  top: 10px;
  left: 50%;
  width: 800px;
  margin-left: -400px;
  z-index: 10;

  img {
    max-height: 210px;
    display: block;
    margin: 0 auto;
    max-width: 800px;
  }
  .hints-default-image {
    cursor: pointer;
    width: 280px;
  }
}
</style>
